import MdEditor from 'react-markdown-editor-lite'
import React, { useCallback, useMemo, useState } from 'react'
import { MarkdownAttributeProps } from 'Types/Attribute.types'
import Button from 'antd/lib/button'
import { convertMarkdownToHTML } from 'Utils/markdown.util'
import './MarkdownAttribute.less'
import { isEmpty } from 'lodash'
import Flex from 'antd/lib/flex'
import ArrowsAltOutlined from '@ant-design/icons/lib/icons/ArrowsAltOutlined'
import EditFilled from '@ant-design/icons/lib/icons/EditFilled'

const RichTextAttribute: React.FC<MarkdownAttributeProps> = props => {
    const [isCollapsed, setIsCollapsed] = useState(true)
    const [isDisabled, setIsDisabled] = useState(true)
    const [lastUpdatedContent, setLastUpdatedContent] = useState(props.value)

    const onClickEditContent = useCallback(() => {
        setLastUpdatedContent(props.value)
        setIsDisabled(false)
        setIsCollapsed(false)
    }, [props.value])

    const onClickCancel = useCallback(() => {
        props.onChange(lastUpdatedContent)
        setIsDisabled(true)
        setIsCollapsed(true)
    }, [lastUpdatedContent, props])

    const isContentEmpty = useMemo(() => {
        return isEmpty(props.value?.trim())
    }, [props.value])

    const onClickSave = useCallback(() => {
        setIsDisabled(true)
        setIsCollapsed(true)
    }, [])

    const onClickViewContent = useCallback(() => {
        setLastUpdatedContent(props.value)
        setIsCollapsed(false)
    }, [props.value])

    const editorHeight = useMemo(() => {
        if (isCollapsed) {
            return isContentEmpty ? '100px' : '120px'
        }
        return '100%'
    }, [isCollapsed, isContentEmpty])

    return (
        <div
            className={`attribute-main ${props.className} markdown-attribute`}
            style={{ width: props.width ?? '90%' }}
            data-testid="markdown-attribute-wrapper"
        >
            <span className="attribute-main-title">{props.label}</span>
            <div
                className={
                    'markdown-attribute-container ' +
                    (isCollapsed ? 'collapsed' : '') +
                    (isContentEmpty ? ' empty' : '') +
                    (isDisabled ? ' disabled' : '')
                }
            >
                <div className="markdown-editor-container">
                    <MdEditor
                        id={props.id}
                        canView={{
                            menu: true,
                            md: true,
                            html: true,
                            both: true,
                            fullScreen: false,
                            hideMenu: false,
                        }}
                        readOnly={props.readonly || isDisabled}
                        view={{ menu: true, md: true, html: true }}
                        value={props.value}
                        style={{ height: editorHeight }}
                        renderHTML={text => {
                            try {
                                return convertMarkdownToHTML(text)
                            } catch (error) {
                                console.log('Markdown processing error: ', error)
                                return '<p>Error rendering content</p>'
                            }
                        }}
                        onChange={({ text }) => props.onChange(text)}
                    />
                    {!isCollapsed && (
                        <div className="markdown-attribute-container-actions">
                            <Button onClick={onClickCancel}>
                                {isDisabled ? 'Close' : 'Cancel'}
                            </Button>
                            {isDisabled ? (
                                <></>
                            ) : (
                                <Button type="primary" onClick={onClickSave}>
                                    Save
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {!isContentEmpty && (
                <Flex
                    justify="flex-end"
                    vertical={false}
                    align="center"
                    className="markdown-attribute-collapsed-actions"
                    gap={10}
                >
                    <Button
                        shape="circle"
                        onClick={onClickViewContent}
                        className={'edit-content-button'}
                    >
                        <ArrowsAltOutlined />
                    </Button>
                    {!props.readonly && (
                        <Button
                            shape="circle"
                            onClick={onClickEditContent}
                            className={'edit-content-button'}
                        >
                            <EditFilled />
                        </Button>
                    )}
                </Flex>
            )}
            {isContentEmpty && (
                <Button
                    disabled={props.readonly}
                    type="primary"
                    onClick={onClickEditContent}
                    className={'add-content-button'}
                >
                    Add Content
                </Button>
            )}
        </div>
    )
}

export default RichTextAttribute
